import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { GlobalFunctions, SnackType, imagePath, imgTypes } from '@global/GlobalFunctions';
import { AssetsDataService, ILevelAsset, LevelAsset } from '@services/assets-data.service';
import { IOrganisationsHierarchy, OrganisationDataService } from '@services/organisation-data.service';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, finalize, Observable, Subject, takeUntil, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { EditHierarchyAssetComponent } from 'src/app/dialogs/edit-level-asset/edit-level-asset.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AssetDataPoint, DataPoint, DatapointsDataService } from '@services/datapoints-data.service';
import { EditSensorDatapointComponent } from 'src/app/dialogs/edit-sensor-datapoint/edit-sensor-datapoint.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { CreateRuleComponent, EditRuleDialogData } from 'src/app/dialogs/create-rule/create-rule.component';
import { Rule, RuleDefinition, RulesDataService } from '@services/rules-data.service';
import { ListBoxComponent } from '@syncfusion/ej2-angular-dropdowns';
import { OrganisationDatapointsComponent } from 'src/app/dialogs/organisation-datapoints/organisation-datapoints.component';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { Router } from '@angular/router';
import { Account, AccountFeatures, AccountsService, IAccount } from '@services/accounts.service';
import { json } from 'node:stream/consumers';

/**
 * Component
 */
@Component({
	selector: 'app-hierarchy-manage-asset',
	templateUrl: './hierarchy-manage-asset.component.html',
	styleUrls: ['./hierarchy-manage-asset.component.scss']
})
export class HierarchyManageAssetComponent implements OnInit, OnChanges {

	/** AccountFeatures */
	AccountFeatures = AccountFeatures;

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.accountID, imgTypes.organisationassets).toLowerCase()
	}

	/** Path to Sensor Images */
	public imagePathSensors(): string {
		return imagePath(this.accountID, imgTypes.sensors).toLowerCase()
	}

	/** inputValue */
	@Input() assetID: string;
	/** hierarchyData */
	@Input() hierarchyData: IOrganisationsHierarchy[];
	/** accountID */
	@Input() accountID: string;
	/** OrganisationID */
	@Input() OrganisationID: string;
	/** hierarchyChanged */
	@Output() hierarchyChanged = new EventEmitter<boolean>();
	/** listbox */
	@ViewChild('listbox') listbox: ListBoxComponent;
	/** selectedAsset */
	public selectedAsset: LevelAsset;
	/** hasActiveRules */
	public hasActiveRules: boolean = false;
	/** ShowHiddenLocations */
	public ShowHiddenLocations: number = 1;

	/** Location Form */
	public assetForm: FormGroup;// { return this.accountFormGroup.controls; }
	/** Form Submitted */
	public submitted: boolean = false;
	/** Form Success */
	public success: any;
	/** Form Error */
	public error: any;

	/** file */
	public file: any
	/** fileName */
	public fileName: string
	/** loading */
	public loading: boolean
	/** shortLink */
	public shortLink: any
	/** assetKnowledgeFiles */
	public assetKnowledgeFiles: any[];
	/** assetDataPoints */
	public assetDataPoints: AssetDataPoint[];
	/** assetRules */
	public assetRules: Rule[];

	/** Used when explaining the rules */
	public conditions: { text: string, value: string }[] = [
		{ text: "More Than", value: ">" },
		{ text: "More Than Or Equal To", value: ">=" },
		{ text: "Less Than", value: "<" },
		{ text: "Less Than Or Equal To", value: "<=" }
		// { text: "Equal To", value: "=" },
		// { text: "Not Equal To", value: "<>" },
	];

	public currentUser: WebAppUser

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** numberOfKPIs */
	public numberOfKPIs: number = 0;

	/** transparency */
	public transparency: number = 0;

	/** backgroundColour */
	public backgroundColour: string = "";

	/** existingConfig */
	public existingConfig: any;

	/** kpiConfig */
	public kpiConfig: string;

	/** SelectedAccount */
	public SelectedAccount: IAccount;

	/**
	 * Constructor
	 * @param globalFunctions globalFunctions
	 * @param assetsDataService assetsDataService
	 * @param organisationDataService organisationDataService
	 * @param dialog dialog
	 * @param formBuilder formBuilder
	 * @param datapointsDataService datapointsDataService
	 * @param rulesDataService rulesDataService
	 * @param siteSubscriptions siteSubscriptions
	 * @param authenticationService authenticationService
	 * @param router router
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		private assetsDataService: AssetsDataService,
		private organisationDataService: OrganisationDataService,
		public dialog: MatDialog,
		private formBuilder: FormBuilder,
		private datapointsDataService: DatapointsDataService,
		private rulesDataService: RulesDataService,
		private siteSubscriptions: siteSubscriptions,
		private authenticationService: AuthenticationService,
		private router: Router
	) {
		try {
			combineLatest([
				this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged())
			]).pipe(
				// Ensure that subscriptions are not null or undefined
				filter(([selectedAccount]) => !!selectedAccount)
			).subscribe({
				next: ([selectedAccount]) => {
					this.SelectedAccount = selectedAccount;
					this.SelectedAccount = this.siteSubscriptions.normalizeFeatures(this.SelectedAccount) as Account;

					this.assetForm = this.formBuilder.group({
						ID: ['', Validators.required],
						Name: [''],
						Description: [''],
						EquipmentCode: [''],
						FunctionLocation: [''],
						ParentFunctionalLocation: ['']
					});
					this.subscribeCurrentUser();
				},
				error: (error) => {
					this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				}
			});
		}
		catch (error) { }
	}

	/**
	 * subscribeCurrentUser
	 */
	subscribeCurrentUser() {
		this.authenticationService.currentUser$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((currentUser) => !!currentUser)
		).subscribe({
			next: (currentUser) => {
				this.currentUser = <WebAppUser>currentUser;
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.currentUser$", false) },
		})
	}

	/**
	 * Hide or unhide a location
	 */
	toggleHidden() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();
		if (this.selectedAsset) {
			this.selectedAsset.Hidden = !this.selectedAsset.Hidden;
		}
		this.assetsDataService.patchOrganisationAssets$(this.accountID, this.OrganisationID, this.selectedAsset).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				this.subscribeOrganisationStructure(this.accountID, this.OrganisationID, this.ShowHiddenLocations)
				this.globalFunctions.DisplaySnackBar({
					message: "Asset Details Updated",
					type: SnackType.Info,
					duration: 1000
				});
			},
			error: (error) => {
				if (error.status == 404) { /* returns a 404 when the asset is hidden because the SP filters on hidden = 0   */
					this.subscribeOrganisationStructure(this.accountID, this.OrganisationID, this.ShowHiddenLocations)
					this.globalFunctions.DisplaySnackBar({
						message: "Asset Details Updated",
						type: SnackType.Info,
						duration: 1000
					});
				}
			}
		});
		this.hierarchyChanged.emit(true)
	}

	/**
	 * Edit the selectedAsset details
	 */
	editAsset(): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...this.selectedAsset };

		// Display the dialog box
		const dialogRef = this.dialog.open(EditHierarchyAssetComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			Object.assign(_edit, result);
			_edit.ParentUID = "";
			this.assetsDataService.patchOrganisationAssets$(this.accountID, this.OrganisationID, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					// this.numberRND = Math.random()
					Object.assign(this.selectedAsset, result);
					this.subscribeOrganisationStructure(this.accountID, this.OrganisationID)
					this.updateForm()
					this.globalFunctions.DisplaySnackBar({
						message: "Asset Details Updated",
						type: SnackType.Info,
						duration: 1000
					});
					this.hierarchyChanged.emit(true)
				}
			});
		});
	}

	/**
	 * create a new rule
	 * @param editAsset
	 */
	createRule(editAsset: ILevelAsset): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...editAsset };

		let dialogData = <EditRuleDialogData>{
			assetItem: _edit
		}

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			//split the rule into the rule-definition composition
			const data1 = {
				Condition: 1,
				DatapointID: result.dataPoint1,
				Limit: result.value1,
				Operator: result.condition1
			}
			let definition1: RuleDefinition = data1;

			let definition2 = undefined;
			if (result.dataPoint2) {
				const data2 = {
					Condition: 2,
					DatapointID: result.dataPoint2,
					Limit: result.value2,
					Operator: result.condition2
				}
				definition2 = data2;
			}

			let data3 = undefined;
			let newRule: Rule;
			if (definition2) {
				data3 = {
					AssetID: editAsset.AssetID,
					RuleDescription: result.ruleDescription,
					RuleText: "[1] " + result.comparison + " [2]",
					RuleMessage: result.ruleMessage,
					RuleEnabled: false,
					CMMSEnabled: false,
					TriggerTime: this.timeToMinutes(result.ruleTime),
					Active: false,
					RuleDef: [definition1, definition2]
				}
				newRule = data3;
			} else {
				data3 = {
					AssetID: editAsset.AssetID,
					RuleDescription: result.ruleDescription,
					RuleText: "[1]",
					RuleMessage: result.ruleMessage,
					RuleEnabled: false,
					CMMSEnabled: false,
					TriggerTime: this.timeToMinutes(result.ruleTime),
					Active: false,
					RuleDef: [definition1]
				}
				newRule = data3;
			}

			this.rulesDataService.postRule$(this.accountID, this.OrganisationID, editAsset.AssetID, newRule, this.currentUser.UserID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					this.updateForm()
					this.hierarchyChanged.emit(true)
					this.GetRules(this.selectedAsset)
					this.globalFunctions.DisplaySnackBar({
						message: "Rule Created",
						type: SnackType.Info,
						duration: 1000
					});
				}
			});
		});
	}

	/**
	 * Get the rules for the Asset
	 * @param asset
	 * @returns
	 */
	GetRules(asset: LevelAsset): Promise<boolean> {

		/** Return a Boolean Promise */
		return new Promise<boolean>((resolve, reject) => {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.rulesDataService.getRules$(this.accountID, this.OrganisationID, asset.AssetID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((rules) => !!rules),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: async (rules: any) => {
					try {
						this.assetRules = rules;
						// this.assetRules = {...rules};
						resolve(true);
					} catch (error) {
						this.globalFunctions.HandleError(error, "Error: this.rulesDataService.getRules$.next", false);
						reject(true);
					}
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.rulesDataService.getRules$", false);
					}
					reject(true);
				},
			});
		});
	}

	/**
	 * Edit an existing rule
	 * @param rule
	 * @param event
	 */
	onEditRule(rule: Rule, event: MouseEvent) {

		event.stopPropagation()
		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...rule };

		let time: string = this.convertMinutesToHHMM(_edit.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(_edit.TriggerTime)[1]

		let dialogData = <EditRuleDialogData>{
			assetItem: this.selectedAsset,
			ruleItem: _edit,
			triggerTime: time,
			comparison: _edit.RuleText.match(/AND|OR/)?.[0] || ""
		}

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			if (result.ruleEnabled == null) {
				rule.RuleEnabled = false;
			} else {
				rule.RuleEnabled = result.ruleEnabled
			}
			rule.RuleDescription = result.ruleDescription
			rule.RuleMessage = result.ruleMessage
			rule.TriggerTime = this.timeToMinutes(result.ruleTime)

			//split the rule into the rule-definition composition

			if (rule.RuleDef) {
				const data1 = {
					RuleDefID: rule.RuleDef[0].RuleDefID,
					RuleID: rule.RuleID,
					Condition: 1,
					DatapointID: result.dataPoint1,
					Limit: result.value1,
					Operator: result.condition1
				}
				let definition1: RuleDefinition = data1;

				let definition2 = undefined;
				/** if we already had another definition */
				if (result.dataPoint2 && rule.RuleDef[1]) {
					const data2 = {
						RuleDefID: rule.RuleDef[1].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
					definition2 = data2;
					/** if a new definition is being added */
				} else if (result.dataPoint2 && !rule.RuleDef[1]) {
					const data2 = {
						RuleID: rule.RuleID,
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
					definition2 = data2;
				}

				if (definition2) {
					rule.RuleText = "[1] " + result.comparison + " [2]";
					rule.RuleDef = [definition1, definition2];

				} else {
					rule.RuleText = "[1]"
					rule.RuleDef = [definition1]
				}
			}
			this.patchRule(rule)
			// if (this.selectedAsset) { this.updateForm }
			// this.hierarchyChanged.emit(true)
		});
	}

	/**
	 * OnRuleEnable
	 * @param rule
	 * @param event
	 */
	OnRuleEnable(rule: Rule, event: MatSlideToggleChange) {
		rule.RuleEnabled = event.checked;
		if (event.checked == false) { rule.Active = false; }
		this.patchRule(rule);
	}

	/**
	 * patchRule
	 * @param rule
	 * @param [deleted=false] 
	 */
	patchRule(rule: Rule, deleted: boolean = false) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.rulesDataService.patchRule$(this.accountID,
			this.OrganisationID, this.selectedAsset.AssetID, rule, this.currentUser.UserID, deleted).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((sqlResult) => !!sqlResult),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (sqlResult: any) => {
					// console.log(sqlResult)
					this.globalFunctions.DisplaySnackBar({
						message: !deleted ? "Rule Updated" : "Rule Deleted",
						type: SnackType.Info,
						duration: 1000
					});
					this.hierarchyChanged.emit(true);
					this.GetRules(this.selectedAsset)
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.rulesDataService.patchRule$", false);
					}
				},
			});
	}

	/**
	 * Delete an existing rule
	 * @param rule
	 * @param event
	 */
	onDeleteRule(rule: Rule, event: MouseEvent) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		event.stopPropagation();
		this.globalFunctions.UserConfirm$("Are you sure you want to delete rule: " + rule.RuleDescription + "?").subscribe(async OKResult => {
			if (OKResult) {
				this.patchRule(rule, true);
				// this.rulesDataService.deleteRule$(this.accountID,
				// this.OrganisationID, rule.RuleID).pipe(
				// 	takeUntil(fetchDataSubject), distinctUntilChanged(),
				// 	filter((sqlResult) => !!sqlResult),
				// 	finalize(() => {
				// 		// Ensure the subscription is terminated and the subject is completed
				// 		fetchDataSubject.next();
				// 		fetchDataSubject.complete();
				// 	})
				// ).subscribe({
				// 	next: (sqlResult: any) => {
				// 		// console.log(sqlResult)
				// 		this.globalFunctions.DisplaySnackBar({
				// 			message: "Rule Deleted",
				// 			type: SnackType.Info,
				// 			duration: 1000
				// 		});
				// 		this.GetRules(this.selectedAsset);
				// 		if (this.selectedAsset) { this.updateForm }
				// 	},
				// 	error: (error) => {
				// 		if (error.status != 404) {
				// 			this.globalFunctions.HandleError(error, "Error: this.rulesDataService.deleteRule$", false);
				// 		}
				// 	},
				// });
			}
		});
	}

	/**
	 * Splits the rule info into a readable array of strings
	 * @param rule
	 * @returns
	 */
	explainRule(rule: Rule): string[] {
		let exp: string[] = []
		// exp.push("RULE WILL TRIGGER IF")
		if (rule.RuleDef) {
			for (let i: number = 0; i < rule.RuleDef?.length; i++) {
				let dp: any = this.getDatapointInAssetDataPoints(rule.RuleDef[i].DatapointID)
				if (dp) {
					let condition = this.getTextFromValue(rule.RuleDef[i].Operator);
					exp.push(dp.DPTitle)
					exp.push("Is " + condition)
					exp.push(rule.RuleDef[i].Limit + dp.SI)
					if (i != rule.RuleDef.length - 1) {
						const result = rule.RuleText.match(/AND|OR/)?.[0] || "";
						exp.push(result)
					} else {
						// exp.push("FOR")
						// exp.push(this.convertMinutesToHHMM(rule.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(rule.TriggerTime)[1])
					}
				}
			}
		}
		return exp
	}

	/**
	 * Converts operator symbols into readable strings
	 * @param value
	 * @returns
	 */
	getTextFromValue(value: string): string | undefined {
		const condition = this.conditions.find(cond => cond.value.toLowerCase() === value.toLowerCase());
		return condition ? condition.text : undefined;
	}

	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr
	 * @returns
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes
	 * @returns
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * Delete selectedAsset
	 */
	deleteAsset() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.globalFunctions.UserConfirm$("Are you sure you want to delete " + this.selectedAsset.Name + "?").subscribe(async OKResult => {
			if (OKResult) {
				this.assetsDataService.deleteAsset$(this.accountID,
					this.OrganisationID, this.selectedAsset.AssetID).pipe(
						takeUntil(fetchDataSubject), distinctUntilChanged(),
						filter((sqlResult) => !!sqlResult),
						finalize(() => {
							// Ensure the subscription is terminated and the subject is completed
							fetchDataSubject.next();
							fetchDataSubject.complete();
						})
					).subscribe({
						next: (sqlResult: any) => {
							console.log(sqlResult)
							this.globalFunctions.DisplaySnackBar({
								message: "Asset Deleted",
								type: SnackType.Info,
								duration: 1000
							});
						},
						error: (error) => {
							if (error.status != 404) {
								this.globalFunctions.HandleError(error, "Error: this.assetsDataService.deleteAsset$", false);
							}
						},
						//complete: () => { console.log("this.assetsDataService.deleteAsset$.subscribe: Complete"); }
					});
				if (this.selectedAsset) { this.updateForm }
				this.hierarchyChanged.emit(true);
			}
		});
	}

	/**
	 * Subscribes to changes in the input values and updates the selected asset object
	 * @param changes
	 */
	ngOnChanges(changes: SimpleChanges) {
		this.assetDataPoints = [];
		if (this.hierarchyData && this.assetID != "") {
			this.selectedAsset = this.organisationDataService.findObjectByUID(this.assetID, this.hierarchyData);
			if (this.selectedAsset) {
				this.updateForm();
				this.GetDataPoints(this.selectedAsset);
				this.GetRules(this.selectedAsset);
				// this.numberOfKPIs = this.selectedAsset.NumberOfKPIs ?? 0;
			}

			if (this.selectedAsset.AssetTileConfig) {
				this.existingConfig = JSON.parse(this.selectedAsset.AssetTileConfig)[0];
				this.numberOfKPIs = this.existingConfig.NumberOfKPIs ?? 0;
				this.backgroundColour = this.existingConfig.BackgroundColour ?? 'rgba(255, 255, 255, 0.65)';
			} else {
				this.existingConfig = undefined;
				this.numberOfKPIs = 0;
				this.backgroundColour = 'rgba(255, 255, 255, 0.65)';
			}
			setTimeout(() => {
				if (this.assetRules) {
					this.checkForTriggeredRules();
				}
			}, 150);
		}
	}

	/**
	 * checkForTriggeredRules
	 * @returns
	 */
	checkForTriggeredRules() {
		if (this.assetRules) {
			for (let i = 0; i < this.assetRules.length; i++) {
				if (this.assetRules[i].Active) {
					this.hasActiveRules = true
					return
				}
			}
		}
		this.hasActiveRules = false;
	}

	/**
	 * Get the Data Points for the Asset
	 * @param asset
	 * @returns
	 */
	async GetDataPoints(asset: LevelAsset): Promise<boolean> {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		/** Return a Boolean Promise */
		return new Promise<boolean>((resolve, reject) => {
			this.assetsDataService.getAssetDataPoints$(this.accountID, this.OrganisationID, asset.AssetID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((dataPoints) => !!dataPoints),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: async (dataPoints: any) => {
					try {
						this.assetDataPoints = dataPoints;
						// this.dataPointIDs = dataPoints.map((x: DataPoint) => x.DataPointID);
						resolve(true);
					} catch (error) {
						this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints.next", false);
						reject(true);
					}
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints$", false);
					} else {
						this.assetDataPoints = [];
					}
					reject(true);
				},
				//complete: () => { console.log("this.AssetsDataService.getAssetDataPoints$.subscribe: Complete"); }
			});
		});
	}

	/**
	 * patchAssetDataPoint
	 * @param data data 
	 * @param event event 
	 */
	patchAssetDataPoint(data: any, event: MatSlideToggleChange, onTile: boolean = false) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		let patchAssetDataPoint: AssetDataPoint = data;
		if (patchAssetDataPoint) {
			if (event.checked) {
				if (!onTile) {
					patchAssetDataPoint.ShowOnChart = true;
				} else {
					patchAssetDataPoint.ShowOnTile = true;
				}
			} else {
				if (!onTile) {
					patchAssetDataPoint.ShowOnChart = false;
				} else {
					patchAssetDataPoint.ShowOnTile = false;
				}
			}
			this.assetsDataService.patchAssetDataPoint$(this.accountID, this.OrganisationID, this.selectedAsset.AssetID,
				patchAssetDataPoint.DataPointID, patchAssetDataPoint).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (response) => {
						let message: string = "";
						this.GetDataPoints(this.selectedAsset);
						if (response.Result == "Error") {
							message = response.Error;
						} else {
							// if (onTile) {
							// 	this.siteSubscriptions.RefreshTileValues(true);
							// }
							message = `DataPoint ${event.checked ? "Unhidden on " : "Hidden on "}${!onTile ? "Chart" : "Tile"}`;
						}
						this.globalFunctions.DisplaySnackBar({
							message: message,
							type: SnackType.Info,
							duration: 1000
						});
					}
				});
		}
	}

	/**
	 * Add a Datapoint
	 */
	addDatapoint() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Display the dialog box
		const dialogRef = this.dialog.open(OrganisationDatapointsComponent, {
			data: {},
		});

		// Update the original Location with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			this.globalFunctions.UserConfirm$("Add the Datapoint: " + result["dataPointNames"] + "?").subscribe(async OKResult => {
				if (OKResult) {
					this.assetsDataService.putAssetDataPoint$(this.accountID,
						this.OrganisationID, this.selectedAsset.AssetID, result["dataPointIDs"]).pipe(
							takeUntil(fetchDataSubject), distinctUntilChanged(),
							filter((sqlResult) => !!sqlResult),
							finalize(() => {
								// Ensure the subscription is terminated and the subject is completed
								fetchDataSubject.next();
								fetchDataSubject.complete();
							})
						).subscribe({
							next: (sqlResult: any) => {
								// console.log(sqlResult)
								if (sqlResult["Error"] != "") {
									this.globalFunctions.DisplaySnackBar({ message: sqlResult["Error"], action: "close", type: SnackType.Error, duration: 0 })
								} else {
									this.globalFunctions.DisplaySnackBar({ message: sqlResult["Result"], action: "" })
								}
								this.GetDataPoints(this.selectedAsset)
							},
							error: (error) => {
								if (error.status != 404) { this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.putAssetDataPoint$", false); }
							},
							//complete: () => { console.log("this.AssetsDataService.putAssetDataPoint$.subscribe: Complete"); }
						});
				}
			});
		});
	}

	/**
	 * Returns full datapoint object from assets list of datapoints when given
	 * a DataPointID, or undefined if the DataPointID does not match
	 * @param datapointID
	 * @returns
	 */
	getDatapointInAssetDataPoints(datapointID: number): DataPoint | undefined {
		return this.assetDataPoints.find((x: DataPoint) => x.DataPointID == datapointID)
	}

	/**
	 * Edit the Datapoint
	 * @param editDatapoint
	 */
	editListDatapoint(data: any): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		let listEditDP: DataPoint = data

		// Create a 'Local'/shallow copy of the Location Information
		var _edit = { ...listEditDP };

		// Display the dialog box
		const dialogRef = this.dialog.open(EditSensorDatapointComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result);
			this.datapointsDataService.patchDataPoint$(this.accountID, this.OrganisationID, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					Object.assign(_edit, result);
					//this.GetDataPoints(this.asset);
					this.globalFunctions.DisplaySnackBar({
						message: "DataPoint Details Updated",
						type: SnackType.Info,
						duration: 1000
					});
					this.GetDataPoints(this.selectedAsset);
				},
				error: (error) => { this.globalFunctions.HandleError(error, "asset-thart Error: his.DatapointsDataService.patchDataPoint$", false) },
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		});
	}

	/**
	 * Delete the Datapoint
	 * @param dataPoint
	 */
	deleteListDatapoint(data: any): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		let dataPoint: DataPoint = data
		this.globalFunctions.UserConfirm$("Delete the Datapoint: " + dataPoint.PVName + "?").subscribe(async OKResult => {
			if (OKResult) {
				this.assetsDataService.deleteAssetDataPoint$(this.accountID,
					this.OrganisationID, this.selectedAsset.AssetID, dataPoint.DataPointID).pipe(
						takeUntil(fetchDataSubject), distinctUntilChanged(),
						filter((sqlResult) => !!sqlResult),
						finalize(() => {
							// Ensure the subscription is terminated and the subject is completed
							fetchDataSubject.next();
							fetchDataSubject.complete();
						})
					).subscribe({
						next: (sqlResult: any) => {
							// console.log(sqlResult)
							this.globalFunctions.DisplaySnackBar({
								message: "Datapoint Removed",
								type: SnackType.Info,
								duration: 1000
							});
							this.GetDataPoints(this.selectedAsset)
						},
						error: (error) => {
							if (error.status != 404) {
								this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.deleteAssetDataPoint$", false);
							}
						},
						//complete: () => { console.log("this.AssetsDataService.deleteAssetDataPoint$.subscribe: Complete"); }
					});
			}
		});
	}


	/**
	 * Repopulates form and refreshes listboxes
	 */
	updateForm() {
		this.assetForm.get("Name")?.setValue(this.selectedAsset?.Name)
		this.assetForm.get("Description")?.setValue(this.selectedAsset?.Description || "")
		this.assetForm.get("ID")?.setValue(this.selectedAsset?.AssetID || "")
		this.assetForm.get("EquipmentCode")?.setValue(this.selectedAsset?.EquipmentCode || "")
		this.assetForm.get("FunctionLocation")?.setValue(this.selectedAsset?.FunctionalLocation || "")
		this.assetForm.get("ParentFunctionalLocation")?.setValue(this.selectedAsset?.ParentFunctionalLocation || "")

		this.error = null;
		this.success = null;
	}

	/**
	 * View the Image Fullsize
	 * @param imageURL
	 */
	viewImage(imageURL: string) {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * onChange
	 * @param fileInput
	 */
	onChange(fileInput: any): void {
		const input = fileInput.target as HTMLInputElement;
		// console.log(this.dialogData.AssetID)
		if (input.files && input.files[0]) {
			this.file = input.files[0];

			const reader = new FileReader();
			reader.onload = (e: ProgressEvent<FileReader>) => {
				const previewImage = document.getElementById("preview-image") as HTMLImageElement;
				if (e.target && e.target.result) {
					previewImage.src = e.target.result as string;
				}
			};
			reader.readAsDataURL(this.file);

			// Extract and store the file name
			this.fileName = this.file.name;
			//console.log("File name:", this.fileName); // You can use this file name as needed
		}
	}

	/**
	 * Upload the selected file
	 */
	onUpload(): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();
		// try {
		// 	/** Toggle the loading element */
		// 	this.loading = !this.loading;

		// 	/**
		// 	 * Upload the file to the server
		// 	 * Save the Systems Container
		// 	 * use the Systems GUID Identifier for the filename
		// 	 */
		// 	this.assetsDataService.uploadFile$("prismpdm", this.file, this.selectedAsset.AssetID.toString(), "assets").pipe(
		// 		takeUntil(fetchDataSubject), distinctUntilChanged(),
		// 		filter((response) => !!response),
		// 		finalize(() => {
		// 			// Ensure the subscription is terminated and the subject is completed
		// 			fetchDataSubject.next();
		// 			fetchDataSubject.complete();
		// 		})
		// 	).subscribe((response: any) => {
		// 		if (typeof (response) === 'object') {
		// 			/** Short link via api response */
		// 			this.shortLink = response.link;
		// 			/** Flag variable */
		// 			this.loading = false;
		// 			this.file = undefined
		// 			this.globalFunctions.DisplaySnackBar({ message: "File Uploaded", action: "", duration: 2000 })

		// 			// Update the Parent Page
		// 			// this.imageUpdated.emit()

		// 		}
		// 	});
		// } catch (error) { this.globalFunctions.HandleError(error, "") }
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		if (this.assetID && this.hierarchyData) {
			this.selectedAsset = this.organisationDataService.findObjectByUID(this.assetID, this.hierarchyData);
		}

		// if (this.selectedAsset) { this.updateForm() }

		if (this.selectedAsset) {
			this.GetDataPoints(this.selectedAsset)
			this.checkForTriggeredRules();

			if (this.selectedAsset.AssetTileConfig) {
				this.existingConfig = JSON.parse(this.selectedAsset.AssetTileConfig)[0];
			} else {
				this.existingConfig = undefined;
			}
		}
	}

	// checkRules(){
	// 	// Select all accordion headers
	// 	const headers: NodeListOf<HTMLElement> = document.querySelectorAll('.e-acrdn-header');

	// 	// Loop through the headers
	// 	headers.forEach((header: HTMLElement) => {
	// 		// Your condition to apply the style
	// 		// console.log(header)
	// 		if (header.outerText.includes('Test')) {
	// 			// Apply background color
	// 			header.style.backgroundColor = 'orange';
	// 		}
	// 	});
	// }

	/**
	* Get the Organisation Structure
	* @param AccountID AccountID
	* @param OrganisationID OrganisationID
	*/
	subscribeOrganisationStructure(AccountID: string, OrganisationUID: string, ShowHidden: number = 0) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		try {
			/** Get the Organisation for the selection */
			this.organisationDataService.getOrganisationStructure$(AccountID, OrganisationUID, ShowHidden).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((structure) => !!structure),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (structure: any) => {
					try {
						/** Update the Organisation */
						var OrganisationStructure = <IOrganisationsHierarchy[]>JSON.parse(structure);
						this.siteSubscriptions.SethierarchicalData(OrganisationStructure);
					} catch (error) { console.error(error); }
				},
				error: (error) => {
					if (error["status"] != 404) {
						this.globalFunctions.HandleError(error, "Error: OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe$", false)
					}
				},
				//complete: () => { console.log("OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe: Complete"); }
			});
		} catch (error) { console.error(error); }
	}

	/**
	 * goToAsset
	 */
	goToAsset() {
		this.siteSubscriptions.SetSelectedAsset(this.selectedAsset);
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationID}/location/${this.selectedAsset.ParentUID}/asset/${this.selectedAsset.AssetID}`]);
	}

	/**
	 * numberOfKPIChange
	 * @param event event  
	 */
	numberOfKPIChange(event: any) {
		this.numberOfKPIs = event.value;
	}

	/**
	 * backgroundColourChange
	 * @param event event 
	 */
	backgroundColourChange(event: any) {
		this.backgroundColour = event.value;
	}

	/**
	 * KPIConfigChange
	 */
	KPIConfigChange(config: string) {
		this.kpiConfig = config;
	}

	/**
	 * patchTileConfiguration
	 */
	patchTileConfiguration() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.patchAssetTileConfiguration$(this.accountID, this.OrganisationID, this.selectedAsset.AssetID,
			this.backgroundColour, this.numberOfKPIs, this.kpiConfig, this.transparency).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((structure) => !!structure),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (result: any) => {
					try {
						if (result[0].Result.includes("OK")) {
							this.globalFunctions.DisplaySnackBar({
								message: "Asset Tile Updated",
								type: SnackType.Info,
								duration: 1000
							});
							this.subscribeOrganisationStructure(this.accountID, this.OrganisationID);
						}
					} catch (error) { console.error(error); }
				},
				error: (error) => {
					if (error["status"] != 404) {
						this.globalFunctions.HandleError(error, "Error: OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe$", false)
					}
				},
				//complete: () => { console.log("OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe: Complete"); }
			});
	}

	/**
	 * deleteTileConfiguration
	 */
	deleteTileConfiguration() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.deleteAssetTileConfiguration$(this.accountID, this.OrganisationID, this.selectedAsset.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((structure) => !!structure),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result: any) => {
				try {
					if (result[0].Result.includes("OK")) {
						this.globalFunctions.DisplaySnackBar({
							message: "Asset Tile Updated",
							type: SnackType.Info,
							duration: 1000
						});
					}
					this.subscribeOrganisationStructure(this.accountID, this.OrganisationID);
				} catch (error) { console.error(error); }
			},
			error: (error) => {
				if (error["status"] != 404) {
					this.globalFunctions.HandleError(error, "Error: OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe$", false)
				}
			},
			//complete: () => { console.log("OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe: Complete"); }
		});
	}

	/**
	 * checkFeature
	 * @param feature feature   
	 * @returns 
	 */
	checkFeature(feature: AccountFeatures): boolean {
		if (this.SelectedAccount && this.SelectedAccount.Features) {
			return AccountsService.checkFeature(feature, this.SelectedAccount)
		}
		return false;
	}
}
